/**
 * Single-file embed entry. Registers `<koko-companion>` and, if the
 * hosting script tag carries a `data-api` attribute (or the host page
 * exposes `window.__KOKO_CONFIG__` for dev), auto-mounts a floating
 * instance into `document.body`. That gives both usage modes described
 * in the plan:
 *
 *   a) zero-config script snippet   → floats itself onto the page
 *   b) explicit <koko-companion>    → declarative placement
 */

import { KokoElement } from "./KokoElement";

interface DevGlobalConfig {
  api: string;
  voice?: boolean;
  greeting?: string;
  model?: string;
  spriteBaseUrl?: string;
  fontBaseUrl?: string;
  audioBaseUrl?: string;
  birdBaseUrl?: string;
  birdVariant?: string;
  /** Optional seed for the studio's procedural side-margin trees. */
  branchSeed?: number;
}

declare global {
  interface Window {
    __KOKO_CONFIG__?: DevGlobalConfig;
  }
}

if (!customElements.get("koko-companion")) {
  customElements.define("koko-companion", KokoElement);
}

function autoMountFromScriptTag(): void {
  // Don't auto-mount if the page has already placed the element itself.
  if (document.querySelector("koko-companion")) return;

  const config = readScriptTagConfig() ?? readWindowConfig();
  if (!config) return;

  const element = document.createElement("koko-companion");
  element.setAttribute("api", config.api);
  element.setAttribute("voice", config.voice === false ? "off" : "on");
  if (config.greeting) element.setAttribute("greeting", config.greeting);
  if (config.model) element.setAttribute("model", config.model);
  if (config.spriteBaseUrl) element.setAttribute("sprite-base-url", config.spriteBaseUrl);
  if (config.fontBaseUrl) element.setAttribute("font-base-url", config.fontBaseUrl);
  if (config.audioBaseUrl) element.setAttribute("audio-base-url", config.audioBaseUrl);
  if (config.birdBaseUrl) element.setAttribute("bird-base-url", config.birdBaseUrl);
  if (config.birdVariant) element.setAttribute("bird-variant", config.birdVariant);
  document.body.appendChild(element);
}

function readScriptTagConfig(): DevGlobalConfig | null {
  const script = document.currentScript;
  if (!(script instanceof HTMLScriptElement)) return null;
  const api = script.getAttribute("data-api");
  if (!api) return null;
  return {
    api,
    voice: script.getAttribute("data-voice") !== "off",
    greeting: script.getAttribute("data-greeting") ?? undefined,
    model: script.getAttribute("data-model") ?? undefined,
    spriteBaseUrl: script.getAttribute("data-sprite-base-url") ?? undefined,
    fontBaseUrl: script.getAttribute("data-font-base-url") ?? undefined,
    audioBaseUrl: script.getAttribute("data-audio-base-url") ?? undefined,
    birdBaseUrl: script.getAttribute("data-bird-base-url") ?? undefined,
    birdVariant: script.getAttribute("data-bird-variant") ?? undefined,
  };
}

function readWindowConfig(): DevGlobalConfig | null {
  const candidate = window.__KOKO_CONFIG__;
  if (!candidate || typeof candidate.api !== "string") return null;
  return candidate;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", autoMountFromScriptTag, { once: true });
} else {
  autoMountFromScriptTag();
}
